import type { FeedLimits } from '../connector/feed-engine/feed-engine';
import { PAUSED_INDEFINITELY } from '../schema-copy/readmarker-helper';
import { formatPauseEnd } from './PauseFeedPopup';
import PauseIcon from './PauseIcon';
import './FeedSettingsBadges.css';

interface FeedSettingsBadgesProps {
  /** Unread tracking ('u' counts unread posts, 'a' just browses); no toggle without onToggleShowRead. */
  showRead?: 'u' | 'a';
  onToggleShowRead?: () => void;
  /** Sort order; no toggle without onToggleSort. */
  sortNewestFirst?: boolean;
  onToggleSort?: () => void;
  limits?: FeedLimits;
  filterActive?: boolean;
  /** Opens the limits dialog; no limits badge without it. */
  onLimits?: () => void;
  /** Opens the keyword filter; no filter badge without it (e.g. plan without filters). */
  onFilter?: () => void;
  /** The feed's pause end (semi), undefined when it isn't paused */
  pausedUntil?: number;
  /** Opens the pause dialog; no pause badge without it. */
  onPause?: () => void;
  /** Also show badges for settings that are off (dashed "+ Limits" / "+ Filter"), to set them. */
  showUnset?: boolean;
  /** Only the icons, no text (the narrow mobile header). */
  iconOnly?: boolean;
  /** Replaces the badges' own actions (onLimits/onFilter/onPause still decide which are shown). */
  onBadgeClick?: (ev: React.MouseEvent) => void;
}

const BookIcon = () => (
  <svg className="feedSettingsBadgeIcon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M8 3.5C6.5 2.3 4.3 2 2.5 2.5v9C4.3 11 6.5 11.3 8 12.5M8 3.5C9.5 2.3 11.7 2 13.5 2.5v9C11.7 11 9.5 11.3 8 12.5M8 3.5v9" />
  </svg>
);

const EyeIcon = () => (
  <svg className="feedSettingsBadgeIcon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1.5 8S3.7 3.5 8 3.5 14.5 8 14.5 8 12.3 12.5 8 12.5 1.5 8 1.5 8Z" />
    <circle cx="8" cy="8" r="2" />
  </svg>
);

const ArrowIcon = ({ up }: { up?: boolean }) => (
  <svg className="feedSettingsBadgeIcon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={up ? 'M8 13V3M4 7l4-4 4 4' : 'M8 3v10M4 9l4 4 4-4'} />
  </svg>
);

const StopwatchIcon = () => (
  <svg className="feedSettingsBadgeIcon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="9.5" r="5" />
    <path d="M8 9.5V7M6.5 2h3M8 2v2.5M12.2 4.8l1-1" />
  </svg>
);

const FunnelIcon = () => (
  <svg className="feedSettingsBadgeIcon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.5 3h11l-4.2 5v4.5l-2.6 1.2V8L2.5 3Z" />
  </svg>
);

/** Blue badges for a feed's unread tracking and sort order (toggles), unread limits ("Max 30/7d"),
 *  keyword filter ("Filtered") and pause ("Paused"); clicking one toggles it or opens its editor. Used in the PostView header and in the FeedTile's
 *  badge row. */
const FeedSettingsBadges: React.FC<FeedSettingsBadgesProps> = ({ showRead, onToggleShowRead, sortNewestFirst, onToggleSort, limits, filterActive, onLimits, onFilter, pausedUntil, onPause, showUnset, iconOnly, onBadgeClick }) => {
  const limitsText = [
    limits?.maxUnread,
    limits?.maxAgeDays && (limits.maxAgeDays == 1 ? '24h' : `${limits.maxAgeDays}d`),
  ].filter(Boolean).join('/');
  // The header's left side toggles the sidebar on click; the badges must not.
  const badge = (active: boolean, icon: React.ReactNode, text: string, title: string, onClick: () => void, className = '') => (
    <button type="button" className={`feedSettingsBadge flowFocussable${active ? '' : ' unset'}${iconOnly && icon ? ' iconOnly' : ''} ${className}`}
      title={title} aria-label={title} onClick={ev => { ev.stopPropagation(); onBadgeClick ? onBadgeClick(ev) : onClick(); }}>
      {icon}{!(iconOnly && icon) && <span className="feedSettingsBadgeText">{text}</span>}
    </button>
  );
  const pauseIcon = <PauseIcon className="feedSettingsBadgeIcon" />;
  return <>
    {onToggleShowRead && (showRead == 'u'
      ? badge(true, <BookIcon />, 'counting',
        'Counts unread posts, so you can read every post. Click to just browse the feed without unread counts.', onToggleShowRead)
      : badge(true, <EyeIcon />, 'not counting',
        'No unread counts: you just browse the feed, and it only gets a dot for new posts. Click to count unread posts.', onToggleShowRead))}
    {onToggleSort && (sortNewestFirst
      ? badge(true, <ArrowIcon />, 'newest', 'Newest posts first. Click to show the oldest posts first.', onToggleSort, 'sortBadge')
      : badge(true, <ArrowIcon up />, 'oldest', 'Oldest posts first, to read the feed in order. Click to show the newest posts first.', onToggleSort, 'sortBadge'))}
    {onLimits && (limitsText || showUnset) && (limitsText
      ? badge(true, <StopwatchIcon />, `Max ${limitsText}`, `Unread posts are limited to ${limitsText}`, onLimits)
      : badge(false, undefined, '+ Limits', 'Limit unread posts', onLimits))}
    {onFilter && (filterActive || showUnset) && (filterActive
      ? badge(true, <FunnelIcon />, 'Filtered', 'Posts are filtered by keywords', onFilter)
      : badge(false, undefined, '+ Filter', 'Filter posts by keywords', onFilter))}
    {onPause && (pausedUntil || showUnset) && (pausedUntil
      ? badge(true, pauseIcon, 'Paused', pausedUntil >= PAUSED_INDEFINITELY ? 'Paused' : `Paused until ${formatPauseEnd(pausedUntil)}`, onPause)
      : badge(false, pauseIcon, 'Pause', 'Pause this feed: mark its posts as read as they arrive', onPause))}
  </>;
};

export default FeedSettingsBadges;
